import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Copy, Mail, Users, Wrench } from "lucide-react";

// One-tap chips that drop pre-written text into the "Notes to receiver"
// textarea. Kept module-level so React doesn't rebuild the list on every
// render. Order = frequency of real usage on the shop floor.
const NOTE_TEMPLATES = [
  { label: "Urgent", text: "Urgent — please expedite this order." },
  {
    label: "Missed at stocktake",
    text: "Missed at last stocktake — please add to next delivery.",
  },
  { label: "Hold until Friday", text: "Hold delivery until Friday please." },
  {
    label: "Split delivery ok",
    text: "Split delivery is fine — send what's available now, backorder the rest.",
  },
  { label: "Confirm ETA", text: "Please confirm ETA on this order." },
  {
    label: "Backorder ok",
    text: "Backorder is acceptable — please confirm expected timing.",
  },
  {
    label: "Match previous",
    text: "Please match the specification of our previous order.",
  },
];

const POSendDialog = ({ open, po, onClose, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [mode, setMode] = useState("supplier");
  const [busy, setBusy] = useState(false);
  const [teamWithEmail, setTeamWithEmail] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  // Sender-typed notes that ride on top of the PO email — used for
  // "Urgent", "missed at last stocktake", "hold delivery until Friday",
  // etc. The word "urgent" (or asap/rush/priority) is auto-mirrored
  // into the subject line by the backend.
  const [sendNotes, setSendNotes] = useState("");
  // Set to true the moment the user hand-edits the To input. While true
  // the chip-selection effects leave To alone (they'd otherwise clobber
  // whatever they typed). Reset on mode-switch and dialog-reopen.
  const [toDirty, setToDirty] = useState(false);

  // Unique set of order_contact_ids across every line item on this PO —
  // these are the teammates who are the go-to person for at least one
  // item on the order. We pre-tick them and float them to the top of the
  // list so the manager doesn't have to hunt for the right names.
  const primaryContactIds = useMemo(() => {
    const seen = new Set();
    (po?.items || []).forEach((it) => {
      if (it.order_contact_id) seen.add(it.order_contact_id);
    });
    return seen;
  }, [po?.items]);

  // Team list re-sorted so the PO's primary contacts appear first, then
  // everyone else. Sorting is memoised — the raw list is otherwise
  // ordered by user creation date which is meaningless here.
  const orderedTeam = useMemo(() => {
    const primary = [];
    const rest = [];
    teamWithEmail.forEach((u) => {
      if (primaryContactIds.has(u.id)) primary.push(u);
      else rest.push(u);
    });
    return [...primary, ...rest];
  }, [teamWithEmail, primaryContactIds]);

  const safePreviewHtml = useMemo(
    () => (preview?.html ? DOMPurify.sanitize(preview.html) : ""),
    [preview?.html],
  );

  useEffect(() => {
    if (!open || !po?.id) return;
    setMode("supplier");
    setCc("");
    setSelectedIds([]);
    setToDirty(false);
    // Companion (toll-processing) POs come with a pre-written heads-up
    // banner that includes the parent supplier's name, log count, wheel
    // yield and cutting fee — see server-side create_po. Pre-fill it so
    // the manager just checks + hits Send. Manager can still edit before
    // sending; the empty-string fallback keeps regular POs pristine.
    setSendNotes(po.default_send_notes || "");
    const supEmail = po.supplier_snapshot?.email || "";
    setTo(supEmail);
    // Pre-load team members so the Ops-forward mode can show a checkbox list
    // of teammates with a stored email — no more manual typing (avoids human
    // error where a PO gets sent to the wrong address).
    api
      .get("/users/public")
      .then((r) => {
        const list = (r.data || []).filter(
          (u) => u.is_active !== false && (u.email || "").trim()
        );
        setTeamWithEmail(list);
      })
      .catch(() => setTeamWithEmail([]));
  }, [open, po]);

  // Live preview — re-fetches whenever the sender notes change (debounced
  // 350 ms so we don't spam the backend on every keystroke). Keeps the
  // right-hand pane truthful so the manager sees exactly what the
  // supplier will read before hitting Send.
  useEffect(() => {
    if (!open || !po?.id) return;
    const t = setTimeout(() => {
      api
        .get(`/po/${po.id}/preview`, { params: { notes: sendNotes || "" } })
        .then((r) => setPreview(r.data))
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [open, po?.id, sendNotes]);

  // When the user switches INTO forward mode, auto-tick every primary
  // order-contact on the PO so the "To" line is filled in immediately.
  // The user can still untick / add manually — this just removes the
  // "who do I send this to?" cognitive step for the common case.
  useEffect(() => {
    if (mode !== "forward" || teamWithEmail.length === 0) return;
    if (primaryContactIds.size === 0) return;
    const availableIds = teamWithEmail
      .map((u) => u.id)
      .filter((id) => primaryContactIds.has(id));
    if (availableIds.length === 0) return;
    setSelectedIds((prev) => (prev.length === 0 ? availableIds : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, teamWithEmail]);

  // Mirror chip selection into the To input while the user hasn't
  // hand-edited it. This effect now runs on EVERY selection change
  // (including the transition to empty) so unticking the last chip
  // clears To instead of leaving a stale address the manager thought
  // they'd removed. If the user has hand-typed a To value (toDirty),
  // the effect stays out of their way.
  useEffect(() => {
    if (mode !== "forward") return;
    if (toDirty) return;
    const emails = selectedIds
      .map((id) => teamWithEmail.find((u) => u.id === id)?.email)
      .filter(Boolean);
    setTo(emails.join(", "));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, mode]);

  if (!open || !po) return null;

  // Append a chip's pre-written line to whatever the manager already typed,
  // separating with a blank line so multi-chip notes read cleanly. If the
  // exact phrase is already in the notes we skip — chip-mashing shouldn't
  // duplicate text.
  const appendNote = (text) => {
    setSendNotes((prev) => {
      const clean = (prev || "").trim();
      if (clean.toLowerCase().includes(text.toLowerCase())) return prev;
      const next = clean ? `${clean}\n${text}` : text;
      // Respect the 1000-char cap even if the user chip-mashes to death.
      return next.length > 1000 ? next.slice(0, 1000) : next;
    });
  };

  const toggleTeam = (id) => {
    // Clicking a chip is a clear signal the user wants to drive To via
    // chips again, so cancel any "hand-typed" dirty flag so the mirror
    // effect can repopulate To from the new selection.
    setToDirty(false);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(preview?.text || "");
      toast.success("Copied PO text to clipboard");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const send = async () => {
    if (!to || !to.includes("@")) return toast.error("Enter a valid recipient email");
    setBusy(true);
    try {
      await api.post(`/po/${po.id}/send`, {
        to,
        cc: cc || undefined,
        mode,
        notes: sendNotes || undefined,
      });
      toast.success(`PO sent to ${to}`);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="po-send-overlay"
    >
      <div
        className="nb-card p-5 w-full max-w-3xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="po-send-dialog"
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="label-eyebrow">
              {po.companion_of ? "Service request" : "Purchase order"}
            </div>
            <h3 className="font-display text-2xl">Send {po.po_number}</h3>
          </div>
          <button onClick={onClose} className="nb-btn bg-white p-2">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {po.companion_of && (
          <div
            className="mb-3 border-2 border-[#7A5300] bg-[#FFF8E1] rounded-lg px-3 py-2 text-xs text-[#3B2A00] flex items-start gap-2"
            data-testid="po-service-request-banner"
          >
            <Wrench size={14} strokeWidth={2.5} className="mt-0.5 shrink-0" />
            <div>
              <b>Service request — not a purchase.</b> This goes to the
              processor as a heads-up for the cutting job on the parent PO.
              Manually confirm timing / drop-off details in the notes below
              before sending.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <button
                data-testid="po-mode-supplier"
                onClick={() => {
                  setMode("supplier");
                  setSelectedIds([]);
                  setToDirty(false);
                  setTo(po.supplier_snapshot?.email || "");
                }}
                className={`nb-btn flex-1 py-2.5 text-sm ${
                  mode === "supplier" ? "nb-btn-primary" : "bg-white"
                }`}
              >
                → Supplier
              </button>
              <button
                data-testid="po-mode-forward"
                onClick={() => {
                  setMode("forward");
                  setTo("");
                  setSelectedIds([]);
                  setToDirty(false);
                }}
                className={`nb-btn flex-1 py-2.5 text-sm ${
                  mode === "forward" ? "nb-btn-primary" : "bg-white"
                }`}
              >
                → Ops (for forward)
              </button>
            </div>
            <div className="label-eyebrow">
              {mode === "supplier"
                ? "Email goes straight to the supplier address."
                : "Email lands in ops mgr inbox so they can forward from a real Footwork address."}
            </div>

            {mode === "forward" && (
              <div
                className="border-2 border-black rounded-xl p-3 bg-white"
                data-testid="po-team-picker"
              >
                <div className="label-eyebrow mb-2 flex items-center gap-1">
                  <Users size={12} strokeWidth={2.5} />
                  Pick team member(s) with an email on file
                </div>
                {mode === "forward" && primaryContactIds.size > 0 && (
                  <div
                    className="text-[11px] text-[#404040] bg-[#FFF7D6] border-2 border-black rounded px-2 py-1.5 mb-2"
                    data-testid="po-primary-hint"
                  >
                    <b>Primary</b> auto-picked from the items on this PO —
                    others are here so you can CC them.
                  </div>
                )}
                {teamWithEmail.length === 0 ? (
                  <div className="text-xs text-[#607087]" data-testid="po-team-empty">
                    No team members have an email set. Add one on the Team page,
                    or type an address manually below.
                  </div>
                ) : (
                    <div className="flex flex-col gap-1.5">
                    {orderedTeam.map((u) => {
                      const active = selectedIds.includes(u.id);
                      const isPrimary = primaryContactIds.has(u.id);
                      return (
                        <button
                          type="button"
                          key={u.id}
                          data-testid={`po-team-chip-${u.id}`}
                          onClick={() => toggleTeam(u.id)}
                          className={`nb-btn px-2.5 py-1.5 text-left flex items-center gap-2 ${
                            active ? "nb-btn-primary" : "bg-white"
                          } ${
                            isPrimary && !active ? "ring-2 ring-[#E5A72E]" : ""
                          }`}
                        >
                          <span
                            className="w-6 h-6 rounded-full border border-black inline-flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 overflow-hidden"
                            style={{ background: u.color || "#228EBF" }}
                          >
                            {u.avatar_url ? (
                              <img
                                src={imageUrl(u.avatar_url)}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              u.name?.[0]?.toUpperCase()
                            )}
                          </span>
                          <span className="flex flex-col min-w-0 flex-1">
                            <span className="text-xs font-semibold truncate flex items-center gap-1.5">
                              {u.name}
                              {isPrimary && (
                                <span
                                  className={`text-[9px] font-black tracking-wider uppercase px-1 py-px rounded ${
                                    active
                                      ? "bg-white text-[#001740]"
                                      : "bg-[#E5A72E] text-black"
                                  }`}
                                  data-testid={`po-team-primary-badge-${u.id}`}
                                >
                                  Primary
                                </span>
                              )}
                            </span>
                            <span
                              className={`text-[11px] truncate ${
                                active ? "text-white/90" : "text-[#607087]"
                              }`}
                            >
                              {u.email}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <label>
              <div className="label-eyebrow mb-1">
                To *{" "}
                {mode === "forward" && selectedIds.length > 0 && (
                  <span className="text-[#607087] normal-case">
                    ({selectedIds.length} selected)
                  </span>
                )}
              </div>
              <input
                data-testid="po-to-input"
                type="email"
                className="nb-input"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  // Mark the field dirty so the chip-selection effect
                  // stops mirroring — otherwise a hand-typed value gets
                  // clobbered on the next chip toggle.
                  setToDirty(true);
                  // If the user is hand-editing away from the chip-selected
                  // set, drop the chip highlights so the "N selected" label
                  // doesn't lie about what will actually be sent.
                  if (mode === "forward" && selectedIds.length > 0) {
                    setSelectedIds([]);
                  }
                }}
                placeholder={
                  mode === "supplier" ? "orders@supplier.com" : "ops@footwork.com.au"
                }
              />
            </label>
            <label>
              <div className="label-eyebrow mb-1">CC (optional)</div>
              <input
                data-testid="po-cc-input"
                type="email"
                className="nb-input"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
              />
            </label>
            <label>
              <div className="label-eyebrow mb-1 flex items-center justify-between">
                <span>Notes to receiver (optional)</span>
                <span className="text-[#607087] normal-case">
                  {sendNotes.length}/1000
                </span>
              </div>
              <div
                className="flex flex-wrap gap-1.5 mb-1.5"
                data-testid="po-send-notes-chips"
              >
                {NOTE_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.label}
                    type="button"
                    data-testid={`po-send-notes-chip-${tpl.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => appendNote(tpl.text)}
                    className="nb-btn bg-white px-2.5 py-1 text-[11px] leading-none whitespace-nowrap"
                    title={tpl.text}
                  >
                    + {tpl.label}
                  </button>
                ))}
              </div>
              <textarea
                data-testid="po-send-notes"
                className="nb-input"
                rows={3}
                maxLength={1000}
                value={sendNotes}
                onChange={(e) => setSendNotes(e.target.value)}
                placeholder='e.g. "Urgent — missed at last stocktake, please expedite"'
              />
              <div className="text-[11px] text-[#607087] mt-1">
                Sits in a yellow banner at the top of the email. Mentioning
                "urgent", "asap" or "rush" also prefixes the subject line.
              </div>
            </label>
            <div className="border-2 border-black rounded-xl p-3 bg-[#DFEEF7]">
              <div className="label-eyebrow">PO Summary</div>
              <div className="font-mono-num text-sm mt-1">
                {po.items.length} line{po.items.length !== 1 ? "s" : ""} · Total $
                {po.total.toFixed(2)} {po.currency}
              </div>
              <div className="text-xs text-[#607087] mt-1">
                Supplier: {po.supplier_snapshot?.name || "—"}
              </div>
            </div>
            <div className="flex gap-2 mt-auto">
              <button
                data-testid="po-copy"
                onClick={copy}
                className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2"
              >
                <Copy size={16} strokeWidth={2.5} /> COPY TEXT
              </button>
              <button
                data-testid="po-send"
                onClick={send}
                disabled={busy || !to}
                className="nb-btn nb-btn-primary px-5 py-2.5 flex items-center gap-2 flex-1 justify-center"
              >
                <Send size={16} strokeWidth={2.5} /> Send
              </button>
            </div>
          </div>

          <div className="flex flex-col min-h-0">
            <div className="label-eyebrow mb-2 flex items-center gap-1">
              <Mail size={12} strokeWidth={2.5} /> Email preview
            </div>
            <div
              className="border-2 border-black rounded-xl bg-white overflow-auto flex-1 scrollbar-clean"
              data-testid="po-preview"
            >
              {preview?.html ? (
                <div
                  className="p-4"
                  dangerouslySetInnerHTML={{ __html: safePreviewHtml }}
                />
              ) : (
                <div className="p-6 text-sm text-[#607087]">Loading preview…</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSendDialog;
